import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store, { URL as API_BASE_URL, URL_CAMV3 } from './store'
import { isFeedInventoryOnly } from './utils/feedInventoryAccess'

const FEED_ONLY_ROUTE = 'FeedInventoryAllBarnsPage'
const FARM_ROLES = ['farm_admin', 'farm_employee']

function settingsReady(companySettings) {
  return Boolean(
    companySettings
    && !Array.isArray(companySettings)
    && companySettings.settings
    && typeof companySettings.settings === 'object'
  )
}

function isSensorPath(path) {
  return path.startsWith('/batch-details')
    || path.startsWith('/batches-history')
    || path.startsWith('/list-users')
}

router.beforeEach((to, from, next) => {
  if (to.name === 'Login' || to.name === 'Recover') {
    next()
    return
  }

  const user = store.getters.getCurrentUser
  const settings = store.getters.getCompanySettings
  const feedOnly = isFeedInventoryOnly(user, settings)

  if (feedOnly) {
    if (to.name === FEED_ONLY_ROUTE) {
      next()
      return
    }
    next({ name: FEED_ONLY_ROUTE })
    return
  }

  // Antes das settings, farm_admin/farm_employee não abrem cam, mic nem feed.
  if (
    FARM_ROLES.includes(user?.role)
    && !settingsReady(settings)
    && isSensorPath(to.path)
  ) {
    next({ name: 'HomePage' })
    return
  }

  next()
})

Vue.config.productionTip = false

const apiOrigin = (value) => {
  try {
    return new window.URL(value).origin
  } catch (e) {
    return ''
  }
}

const API_ORIGINS = [API_BASE_URL, URL_CAMV3].map(apiOrigin).filter(Boolean)

const requestUrlOf = (resource) => {
  if (typeof resource === 'string') return resource
  if (resource && typeof resource.url === 'string') return resource.url
  return ''
}

const isApiRequest = (url) => {
  const origin = apiOrigin(url)
  return origin !== '' && API_ORIGINS.includes(origin)
}

const isLoginRequest = (url) => typeof url === 'string' && url.includes('/auth/login')

// A rejected token used to fail one request at a time and leave the session
// half-open. Reset it. WebKit (Safari, and Chrome on iPhone) can also replay a
// cached 401 for the same API URL after the PWA worker was installed. A private
// tab starts with an empty cache, so the same login works there.
// cache: 'no-store' skips that HTTP cache. Do not add a Cache-Control request
// header: the API preflight does not allow it, and the call never leaves the browser.
const nativeFetch = window.fetch.bind(window)
window.fetch = async (...args) => {
  let [resource, options] = args
  const requestUrl = requestUrlOf(resource)
  let fetchArgs = args

  if (isApiRequest(requestUrl)) {
    options = { ...(options || {}), cache: 'no-store' }
    fetchArgs = [resource, options]
  }

  const response = await nativeFetch(...fetchArgs)

  if (
    response.status === 401 &&
    isApiRequest(requestUrl) &&
    !isLoginRequest(requestUrl) &&
    window.localStorage.getItem('Token')
  ) {
    window.localStorage.clear()
    store.commit('setDefaultValues')
    if (router.currentRoute.path !== '/') {
      router.push('/')
    }
  }
  return response
}

function currentLocation() {
  return new window.URL(window.location.href)
}

function stripRetiredQuery() {
  try {
    const url = currentLocation()
    if (!url.searchParams.has('sw-retired')) return
    url.searchParams.delete('sw-retired')
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  } catch (e) {
    // A malformed URL should not block startup.
  }
}

function retiredAttempt() {
  try {
    const raw = Number(currentLocation().searchParams.get('sw-retired'))
    return Number.isFinite(raw) && raw > 0 ? raw : 0
  } catch (e) {
    return 0
  }
}

function reloadBypassingDocumentCache(attempt) {
  const url = currentLocation()
  url.searchParams.set('sw-retired', String(attempt))
  window.location.replace(url.toString())
}

// Normal Safari and iOS Chrome tabs are still controlled by the PWA worker
// registered before it was removed from the app. Deleting sw.js does not
// unregister it: the update receives the SPA shell (HTTP 200) and WebKit keeps
// the old worker. Private tabs never had that worker. While it controls the
// page, WebKit drops the auth/login POST body and the API answers 401.
async function retireServiceWorker() {
  const container = window.navigator.serviceWorker
  if (!container || typeof container.getRegistrations !== 'function') {
    stripRetiredQuery()
    return false
  }

  let registrations = []
  try {
    registrations = await container.getRegistrations()
  } catch (e) {
    registrations = []
  }

  const controlled = Boolean(container.controller) || registrations.length > 0

  if (window.caches && typeof window.caches.keys === 'function') {
    try {
      const keys = await window.caches.keys()
      await Promise.all(keys.map((key) => window.caches.delete(key)))
    } catch (e) {
      // Cache Storage is unavailable in some private sessions.
    }
  }

  if (controlled) {
    await Promise.all(registrations.map((registration) => {
      const update = typeof registration.update === 'function'
        ? registration.update().catch(() => {})
        : Promise.resolve()
      return Promise.race([
        update,
        new Promise((resolve) => setTimeout(resolve, 3000))
      ])
    }))
  }

  await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)))

  if (!controlled) {
    stripRetiredQuery()
    return false
  }

  // The query is the reload counter. A timestamp set by sw.js is already > 2,
  // so a tab that the worker itself refreshed is not refreshed again.
  const attempt = retiredAttempt()
  if (attempt >= 2) {
    stripRetiredQuery()
    return false
  }

  window.__pecsmartSwReload = true
  reloadBypassingDocumentCache(attempt + 1)
  return true
}

function mountApp() {
  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount('#app')
}

function mountOnce() {
  if (window.__pecsmartMounted || window.__pecsmartSwReload) return
  window.__pecsmartMounted = true
  mountApp()
}

retireServiceWorker().then((reloading) => {
  if (reloading) {
    window.__pecsmartSwReload = true
    return
  }
  mountOnce()
}).catch(() => {
  stripRetiredQuery()
  mountOnce()
})

// getRegistrations can stall on a broken worker. Boot anyway.
setTimeout(mountOnce, 4000)
